import React from "react";
import { useTranslation } from "react-i18next";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { Image, MessageCircle, User } from "lucide-react";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { useOrg } from "@/hooks/useOrg";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";
import { CircleIcon } from "@/components/DashboardOverviewDataCells/CircleIcon";

export const FeedPostDataCells = () => {
  const { t } = useTranslation();
  const { org } = useOrg();
  const {
    loading: metricsLoading,
    error: metricsError,
    getPreviousCycleValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(org?.id, OrganizationMetricsContextEnum.FeedPosts);

  const hasRequiredData = Boolean(org?.id);
  const hasError = metricsError || (!hasRequiredData && !metricsLoading);

  const showLoading = () => {
    if (hasError) return false;
    return metricsLoading;
  };

  const showError = () => {
    return hasError;
  };

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("home.overview.dataCells.error.loadingError")}
    </span>
  );

  return (
    <div>
      {/* Title Section */}
      <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
        {t("feedPost.bento.title")}
      </h2>

      {/* Data Cells Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Official Posts DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("feedPost.bento.officialPosts")}
          tooltipText={t("feedPost.bento.officialPostsToolTip")}
          middleValue={
            showError() ? errorContent : getMetricDisplayValue("officialPost")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("officialPost")}
                prevCount={getPreviousCycleValue("officialPost")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="orange">
              <Image />
            </CircleIcon>
          }
        />

        {/* Official Post Views DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("feedPost.bento.officialPostViews")}
          tooltipText={t("feedPost.bento.officialPostViewsToolTip")}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("officialPostViews")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("officialPostViews")}
                prevCount={getPreviousCycleValue("officialPostViews")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="emerald">
              <MessageCircle />
            </CircleIcon>
          }
        />

        {/* Featured Student Posts DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("feedPost.bento.featuredStudentPosts")}
          tooltipText={t("feedPost.bento.featuredStudentPostsToolTip")}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("featuredStudentPost")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("featuredStudentPost")}
                prevCount={getPreviousCycleValue("featuredStudentPost")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="yellow">
              <User />
            </CircleIcon>
          }
        />

        {/* Featured Student Post Views DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("feedPost.bento.featuredStudentPostViews")}
          tooltipText={t("feedPost.bento.featuredStudentPostViewsToolTip")}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("featuredStudentPostViews")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged(
                  "featuredStudentPostViews",
                )}
                prevCount={getPreviousCycleValue("featuredStudentPostViews")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="violet">
              <User />
            </CircleIcon>
          }
        />
      </div>
    </div>
  );
};
